// Shared bits for the Invoices page (kitchen and bar supplier invoices, cost of goods).
import { useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';

export type Dept = 'kitchen' | 'bar' | 'other';
export type Category = 'food' | 'drinks' | 'other';
export type InvoiceStatus = 'reading' | 'review' | 'confirmed' | 'failed';

export interface InvoiceRow {
  id: string;
  department: Dept;
  supplier_name: string | null;
  invoice_number: string | null;
  invoice_date: string;
  date_guessed: boolean;
  subtotal: number | null;
  tax: number | null;
  total: number | null;
  file_path: string | null;
  file_mime: string | null;
  status: InvoiceStatus;
  source: 'scan' | 'manual';
  notes: string | null;
  error: string | null;
  created_at: string;
}

export interface LineRow {
  id?: string;
  position: number;
  name: string;
  qty: number | null;
  unit: string | null;
  unit_price: number | null;
  line_total: number | null;
  category: Category;
}

export interface CostOfGoods {
  food_sales: number | null; drinks_sales: number | null; nights: number;
  food_spend: number; drinks_spend: number; other_spend: number;
  invoices: number; to_check: number;
  food_target: number | null; drinks_target: number | null;
}

/** Positions that get invoices automatically (same list as invoice_position_ok in the database). */
export const INVOICE_POSITIONS = ['head chef', 'executive chef', 'bar manager', 'head bartender'];
export const invoicePosition = (p: string | null | undefined) => INVOICE_POSITIONS.includes((p ?? '').trim().toLowerCase());

export const INVOICE_COLS = 'id, department, supplier_name, invoice_number, invoice_date, date_guessed, subtotal, tax, total, file_path, file_mime, status, source, notes, error, created_at';

export const CATEGORY_LABEL: Record<Category, string> = { food: 'Food', drinks: 'Drinks', other: 'Other' };
export const DEPT_LABEL: Record<Dept, string> = { kitchen: 'Kitchen', bar: 'Bar', other: 'Other' };

export const money2 = (n: number | null | undefined) =>
  n == null || !Number.isFinite(n) ? '—' : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const addDays = (s: string, n: number) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() + n); return isoDay(d); };
export const mondayOf = (s: string = isoDay(new Date())) => { const d = new Date(`${s}T12:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return isoDay(d); };
export const shortDate = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
export const dayLabel = (s: string) => new Date(`${s}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

/** cost ÷ sales as a %, one decimal. */
export const pct = (cost: number, sales: number | null | undefined) => (sales && sales > 0 ? Math.round((cost / sales) * 1000) / 10 : null);

/** Owners and managers always; anyone else only if they were given invoice access. */
export function useInvoiceAccess(venueId: string | null, userId: string | null, role: string) {
  const mgr = role === 'owner' || role === 'manager';
  const [ok, setOk] = useState<boolean>(mgr);
  useEffect(() => {
    if (mgr) { setOk(true); return; }
    if (!venueId || !userId) { setOk(false); return; }
    let off = false;
    void Promise.all([
      supabase.from('invoice_access').select('user_id').eq('venue_id', venueId).eq('user_id', userId).maybeSingle(),
      supabase.from('users').select('position').eq('id', userId).maybeSingle(),
    ]).then(([a, u]) => { if (!off) setOk(!!a.data || invoicePosition(u.data?.position as string | null)); });
    return () => { off = true; };
  }, [venueId, userId, mgr]);
  return ok;
}

/** Photos are turned into a reasonably sized JPEG before upload. iPhone HEIC photos are converted first (Chrome can't open them). */
export async function prepareFile(input: File): Promise<{ blob: Blob; mime: string; ext: string }> {
  if (input.type === 'application/pdf' || /\.pdf$/i.test(input.name)) return { blob: input, mime: 'application/pdf', ext: 'pdf' };
  let file: Blob = input;
  if (/heic|heif/i.test(input.type) || /\.(heic|heif)$/i.test(input.name)) {
    try {
      const heic2any = (await import('heic2any')).default;
      const out = await heic2any({ blob: input, toType: 'image/jpeg', quality: 0.9 });
      file = Array.isArray(out) ? out[0] : out;
    } catch { /* Safari can open HEIC itself below */ }
  }
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const max = 2400;
    const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * k); canvas.height = Math.round(img.naturalHeight * k);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (blob) return { blob, mime: 'image/jpeg', ext: 'jpg' };
  } catch { /* fall through */ }
  // Couldn't open it here. JPEG / PNG / WebP can go as they are; anything else can't be read.
  if (/^image\/(jpeg|png|webp)$/.test(file.type)) return { blob: file, mime: file.type, ext: file.type.split('/')[1].replace('jpeg', 'jpg') };
  throw new Error("Couldn't open that photo. Take it with the camera button, or save it as a JPG or PDF and upload that.");
}

/** Friendlier message when the database update hasn't been run yet. */
export const dbMsg = (m: string) => /invoices|invoice_lines|invoice_access|invoice_settings|cost_of_goods|schema cache/.test(m) && /does not exist|schema cache|Could not find/.test(m)
  ? `Invoices need the latest database update (20261028090000_invoices.sql). (${m})` : m;
